import React from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";

import { ProductListView } from "./productList/ProductListView";
import { IProduct } from "../../../interfaces/IProduct";
import ISubCategory from "../categories/ISubCategory";
import { menuItemAPICall } from "./ItemsContainer";
import { useVenueData } from "../../../hooks/useVenueData";
import { useTranslation } from "react-i18next";
import { useMediaQuery, useTheme } from "@material-ui/core";
import { FIELD_TYPES } from "../../../yoello-lib/modules/components/draggable-list";
import Truncate from "react-truncate";
import useFormatCurrency from "../../../yoello-lib/modules/localisation/useCurrency";
import { HTTPMethods } from "../../../yoello-lib/modules/API/API";
import DotMenu from "../../../yoello-lib/modules/components/DotMenu";
import IMenuItemBody from "../../../interfaces/IMenuItemBody";
import useCheckPermissions from "../../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../../const/permissions.const";
import { useAPIV2 } from "../../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../../yoello-lib/modules/API2/APIDefs";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    draggableMenuItems: {
      "&.draggable": {
        margin: "1rem 0",
      },

      "& .draggable-card": {
        display: "flex",
        alignItems: "stretch",
        flexDirection: "row",
        marginBottom: "15px",
        cursor: "move",

        "&:hover": {
          "& .draggable-item": {
            top: "-2px",
            left: "-2px",
            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
          },
        },
      },

      "& .draggable-handler": {
        width: "4.375rem",
        backgroundImage: 'url("/assets/images/icon-handler.svg")',
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        flexShrink: "0",
        display: "block",
        "@media only screen and (max-width: 600px)": {
          display: "none",
        },
      },

      "& .draggable-onDrag": {
        width: "4.375rem",
        backgroundImage: 'url("/assets/images/icon-dnd-touch.svg")',
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        flexShrink: 0,
      },

      "& .draggable-item": {
        position: "relative",
        flexGrow: 10,
        display: "grid !important",
        gridGap: 0,
        backgroundColor: "#ffffff",
        boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.2)",
        borderRadius: "10px",
        overflow: "hidden",
        cursor: "pointer",
        gridTemplateColumns:
          "[start] 8rem [name_public] auto [enabled] 6rem [end]",
        gridTemplateRows: "[start] auto [price] auto [end]",
        transition: "all 0.5s",

        "@media only screen and (min-width: 1280px)": {
          gridTemplateColumns:
            "[start] 8rem [name_public] 1fr [description] 2fr [price] 6rem [enabled] 6rem [end]",
        },
      },

      "& .draggable-url_photo": {
        gridColumn: "start/name_public",
        gridRow: "start/end",
        backgroundImage: 'url("/assets/images/image-placeholder.svg")',
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        height: "8rem",

        "@media only screen and (min-width: 1280px)": {
          gridColumn: "start/name_public",
          gridRow: "start/end",
        },
      },

      "& .draggable-name_public": {
        gridColumn: "name_public/enabled",
        gridRow: "start/end",
        padding: "1rem",
        fontSize: "1rem",
        display: "flex",
        alignItems: "center",

        "@media only screen and (min-width: 1280px)": {
          gridColumn: "name_public/description",
          gridRow: "start/end",
          paddingLeft: "3rem",
        },
      },

      "& .draggable-description": {
        padding: "1rem",
        color: "#9e9e9e",
        overflow: "hidden",
        display: "none",
        fontSize: "1rem",

        "@media only screen and (min-width: 1280px)": {
          gridColumn: "description/price",
          gridRow: "start/end",
          display: "flex",
          alignItems: "center",
        },
      },

      "& .draggable-price": {
        gridColumn: "enabled/end",
        gridRow: "start/price",
        padding: "1rem",
        fontSize: "1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        "@media only screen and (min-width: 1280px)": {
          gridColumn: "price/enabled",
          gridRow: "start/end",
        },
      },

      "& .draggable-enabled": {
        gridColumn: "enabled/end",
        gridRow: "price/end",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        "@media only screen and (min-width: 1280px)": {
          gridColumn: "enabled/end",
          gridRow: "start/end",
        },
      },
    },
  })
);

interface IItemProductListViewProps {
  venueId: string;
  productList: IProduct[];
  onItemSelection: (product: IProduct) => void;
  subcategories: ISubCategory[];
  menuItemAPICall: menuItemAPICall;
  isLoading: boolean;
  onSortedItem: (positions: string[]) => void;
}

export function ItemProductListView({
  venueId,
  productList,
  onItemSelection,
  subcategories,
  menuItemAPICall,
  isLoading,
  onSortedItem,
}: IItemProductListViewProps) {
  const classes = useStyles();
  const theme = useTheme();
  const { isEposNow, isSquare } = useVenueData(venueId);
  const lg = useMediaQuery(theme.breakpoints.up("lg"));
  const xl = useMediaQuery(theme.breakpoints.up("xl"));
  const decsWidth = xl ? 600 : lg ? 360 : 240;
  const { t } = useTranslation();
  const {hasPermission , onFailedInteraction} = useCheckPermissions(PERMISSIONS.MENU_MANAGEMENT)
  const merchantType = isEposNow ? "Epos Now" : isSquare ? "Square" : null;
  const { formatCurrency } = useFormatCurrency();
  
  const { callAPI: sortMenuItems } = useAPIV2<any>(
    APIExtensions.menusItems,
    {
      onMountDisabled: true,
      errorCall: t("menu.menuItems.sortFail"),
      successCall: t("menu.menuItems.sortSuccess"),
      query: "sort",
      yoelloAPIParams: { venue_id: venueId, size: 200, include_disabled: true },
      isPaginated: true
    }
  );

  return (
    <ProductListView
      listClassName={classes.draggableMenuItems}
      isLoading={isLoading}
      productList={productList}
      onItemSelection={onItemSelection}
      sortAPICall={sortMenuItems}
      parentEntry={subcategories}
      onSortedItem={onSortedItem}
      merchantType={merchantType}
      emptyEntryText="menu.products.emptySubCat"
      emptyEntryTextInfo={
        isSquare || isEposNow
          ? "menu.products.emptySubCatInfoEpos_Square"
          : "menu.products.emptySubCatInfo"
      }
      emptyListText="menu.products.emptyProducts"
      emptyListTextInfo={
        isSquare || isEposNow
          ? "menu.products.emptyProductsInfoEpos_Square"
          : "menu.products.emptyProductsInfo"
      }
      fieldList={[
        ...listView(decsWidth, formatCurrency),
        {
          key: "enabled",
          modifier: (enabled: boolean, cardData: IMenuItemBody) => {
            let name: string;
            if (enabled) {
              name = "Remove from stock";
            } else {
              name = "Restore Item";
            }
            const options = [
              {
                name,
                onClick: async () => {
                  if(!hasPermission){
                    onFailedInteraction();
                  }else{
                  await menuItemAPICall(HTTPMethods.PUT, {
                    query: cardData.id,
                    body: { venue_id: venueId, enabled: !enabled },
                  });
                  menuItemAPICall(HTTPMethods.GET);}
                },
              },
            ];
            return (
                <DotMenu key={cardData.id!} id={cardData.id!} options={options} />
            );
          },
        },
      ]}
    />
  );
}

function listView(
  descWidth: number,
  formatCurrency: (valueInSubunit: number, subunit?: boolean) => string
) {
  const windowWidth = window.innerWidth;
  if (windowWidth > 1280) {
    return [
      {
        key: "url_photo",
        type: FIELD_TYPES.IMAGE,
      },
      {
        key: "name_public",
      },
      {
        key: "description",
        modifier: (description: string) => (
          <Truncate lines={4} ellipsis="..." width={descWidth}>
            {description}
          </Truncate>
        ),
      },
      {
        key: "price",
        modifier: (pence: string) =>
          pence === undefined ? "No price" : formatCurrency(parseInt(pence)),
      },
    ];
  } else {
    return [
      {
        key: "url_photo",
        type: FIELD_TYPES.IMAGE,
      },
      {
        key: "name_public",
      },

      {
        key: "price",
        modifier: (pence: string) =>
          pence === undefined ? "No price" : formatCurrency(parseInt(pence)),
      },
    ];
  }
}
